import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, timer } from 'rxjs';
import { catchError, retry, timeout, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
  pagination?: PaginationInfo;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export interface ApiError {
  code: string;
  message: string;
  details?: any;
}

export interface RequestConfig {
  timeout?: number;
  retries?: number;
  cacheable?: boolean;
  skipAuth?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private readonly API_BASE_URL = environment.apiUrl || 'https://localhost:7258';
  private readonly DEFAULT_TIMEOUT = 30000; // 30 seconds
  private readonly DEFAULT_RETRIES = 3;
  
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private requestCacheMap = new Map<string, any>();
  
  loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Generic GET request
   */
  get<T>(
    endpoint: string, 
    params?: HttpParams | { [key: string]: any }, 
    config?: RequestConfig
  ): Observable<T> {
    const url = `${this.API_BASE_URL}${endpoint}`;
    const cacheKey = this.generateCacheKey('GET', url, params);
    
    // Check cache if enabled
    if (config?.cacheable && this.requestCacheMap.has(cacheKey)) {
      return new Observable(observer => {
        observer.next(this.requestCacheMap.get(cacheKey));
        observer.complete();
      });
    }

    this.setLoading(true);
    
    const httpParams = params instanceof HttpParams ? params : new HttpParams({ fromObject: params || {} });
    const headers = this.getHeaders(config?.skipAuth);
    
    return this.http.get<T>(url, { 
      params: httpParams, 
      headers 
    }).pipe(
      timeout(config?.timeout || this.DEFAULT_TIMEOUT),
      retry(config?.retries || this.DEFAULT_RETRIES),
      tap(response => {
        if (config?.cacheable) {
          this.requestCacheMap.set(cacheKey, response);
          // Clear cache after 5 minutes
          timer(300000).subscribe(() => this.requestCacheMap.delete(cacheKey));
        }
      }),
      catchError(this.handleError.bind(this)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Generic POST request
   */
  post<T>(
    endpoint: string, 
    body: any, 
    config?: RequestConfig
  ): Observable<T> {
    const url = `${this.API_BASE_URL}${endpoint}`;
    
    this.setLoading(true);
    
    const headers = this.getHeaders(config?.skipAuth);
    
    return this.http.post<T>(url, body, { headers }).pipe(
      timeout(config?.timeout || this.DEFAULT_TIMEOUT),
      retry(config?.retries || 1), // Less retries for POST
      catchError(this.handleError.bind(this)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Generic PUT request
   */
  put<T>(
    endpoint: string, 
    body: any, 
    config?: RequestConfig
  ): Observable<T> {
    const url = `${this.API_BASE_URL}${endpoint}`;
    
    this.setLoading(true);
    
    const headers = this.getHeaders(config?.skipAuth);
    
    return this.http.put<T>(url, body, { headers }).pipe(
      timeout(config?.timeout || this.DEFAULT_TIMEOUT),
      retry(config?.retries || 1),
      catchError(this.handleError.bind(this)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Generic PATCH request
   */
  patch<T>(
    endpoint: string, 
    body: any, 
    config?: RequestConfig
  ): Observable<T> {
    const url = `${this.API_BASE_URL}${endpoint}`;
    
    this.setLoading(true);
    
    const headers = this.getHeaders(config?.skipAuth);
    
    return this.http.patch<T>(url, body, { headers }).pipe(
      timeout(config?.timeout || this.DEFAULT_TIMEOUT),
      retry(config?.retries || 1),
      catchError(this.handleError.bind(this)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Generic DELETE request
   */
  delete<T>(
    endpoint: string, 
    config?: RequestConfig
  ): Observable<T> {
    const url = `${this.API_BASE_URL}${endpoint}`;
    
    this.setLoading(true);
    
    const headers = this.getHeaders(config?.skipAuth);
    
    return this.http.delete<T>(url, { headers }).pipe(
      timeout(config?.timeout || this.DEFAULT_TIMEOUT),
      retry(config?.retries || 1),
      catchError(this.handleError.bind(this)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Upload file with progress tracking
   */
  uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: { [key: string]: any }
  ): Observable<T> {
    const url = `${this.API_BASE_URL}${endpoint}`;
    const formData = new FormData();
    
    formData.append('file', file);
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    this.setLoading(true);
    
    const headers = this.getHeaders(true, false); // Skip auth check, don't set Content-Type
    
    return this.http.post<T>(url, formData, { headers }).pipe(
      timeout(120000), // 2 minutes for file uploads
      catchError(this.handleError.bind(this)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Clear request cache
   */
  clearCache(pattern?: string): void {
    if (pattern) {
      const keysToDelete = Array.from(this.requestCacheMap.keys())
        .filter(key => key.includes(pattern));
      keysToDelete.forEach(key => this.requestCacheMap.delete(key));
    } else {
      this.requestCacheMap.clear();
    }
  }

  /**
   * Check if request is currently loading
   */
  isLoading(): boolean {
    return this.loadingSubject.value;
  }

  private getHeaders(skipAuth = false, setContentType = true): HttpHeaders {
    let headers = new HttpHeaders();
    
    if (setContentType) {
      headers = headers.set('Content-Type', 'application/json');
    }
    
    if (!skipAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    }
    
    // Add other common headers
    headers = headers.set('Accept', 'application/json');
    headers = headers.set('X-Requested-With', 'XMLHttpRequest');
    
    return headers;
  }

  private getAuthToken(): string | null {
    // TODO: Implement token retrieval from auth service
    return localStorage.getItem('access_token');
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    this.setLoading(false);
    
    let apiError: ApiError = {
      code: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred'
    };

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      apiError = {
        code: 'CLIENT_ERROR',
        message: error.error.message,
        details: error
      };
    } else {
      // Server-side error
      switch (error.status) {
        case 400:
          apiError = {
            code: 'BAD_REQUEST',
            message: error.error?.message || 'Bad request',
            details: error.error
          };
          break;
        case 401:
          apiError = {
            code: 'UNAUTHORIZED',
            message: 'Authentication required',
            details: error.error
          };
          // TODO: Redirect to login or refresh token
          break;
        case 403:
          apiError = {
            code: 'FORBIDDEN',
            message: 'Access denied',
            details: error.error
          };
          break;
        case 404:
          apiError = {
            code: 'NOT_FOUND',
            message: 'Resource not found',
            details: error.error
          };
          break;
        case 422:
          apiError = {
            code: 'VALIDATION_ERROR',
            message: 'Validation failed',
            details: error.error
          };
          break;
        case 500:
          apiError = {
            code: 'SERVER_ERROR',
            message: 'Internal server error',
            details: error.error
          };
          break;
        case 503:
          apiError = {
            code: 'SERVICE_UNAVAILABLE',
            message: 'Service temporarily unavailable',
            details: error.error
          };
          break;
        default:
          apiError = {
            code: `HTTP_${error.status}`,
            message: error.message || 'Request failed',
            details: error.error
          };
      }
    }

    console.error('API Error:', apiError);
    return throwError(() => apiError);
  }

  private setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  private generateCacheKey(method: string, url: string, params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${method}:${url}:${paramString}`;
  }
}